import React, { HTMLAttributes, ReactNode } from 'react';

import clsx from 'clsx';

import { Badges, BadgesProps } from '../Badges';
import { Text } from '../Text';
import {
  tabsContainer,
  tabItem,
  tabItemFrame,
  tabItemText,
  tabItemIcon,
  tabItemIndicator,
} from './Tabs.css';

// 구조화된 Props를 사용하는 아이템
interface StructuredTabItem {
  /** 아이템의 고유 값 */
  value: string;
  /** 표시할 텍스트 */
  text?: string;
  /** 표시할 아이콘 */
  icon?: React.ReactNode;
  /** 배지 속성 */
  badge?: BadgesProps;
  /** 비활성화 여부 */
  disabled?: boolean;
}

// 커스텀 children을 사용하는 아이템
interface CustomTabItem {
  /** 아이템의 고유 값 */
  value: string;
  /** 커스텀 렌더링할 children */
  children: ReactNode;
  /** 비활성화 여부 */
  disabled?: boolean;
}

// 두 타입의 Union
export type TabItemType = StructuredTabItem | CustomTabItem;

// Type guard 함수
const isCustomItem = (item: TabItemType): item is CustomTabItem => {
  return 'children' in item;
};

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** 탭 아이템 목록 */
  items: TabItemType[];
  /** 선택된 값 */
  value?: string;
  /** 값 변경 이벤트 핸들러 */
  onChange?: (value: string) => void;
  /** 전체 비활성화 여부 */
  disabled?: boolean;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  value,
  onChange,
  disabled = false,
  className,
  ...props
}) => {
  const handleItemClick = (itemValue: string, itemDisabled?: boolean) => {
    if (disabled || itemDisabled) {
      return;
    }
    onChange?.(itemValue);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    itemValue: string,
    itemDisabled?: boolean,
  ) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !itemDisabled) {
      e.preventDefault();
      onChange?.(itemValue);
    }
  };

  const renderItemContent = (item: TabItemType, isSelected: boolean, isDisabled: boolean) => {
    // Custom children을 사용하는 경우
    if (isCustomItem(item)) {
      return item.children;
    }

    // 기존 구조화된 방식
    return (
      <>
        {item.icon && <div className={tabItemIcon}>{item.icon}</div>}
        {item.text && (
          <Text
            textStyle="14_400"
            className={tabItemText({ selected: isSelected, disabled: isDisabled })}
          >
            {item.text}
          </Text>
        )}
        {item.badge && <Badges size="s" type="red" {...item.badge} />}
      </>
    );
  };

  return (
    <div className={clsx(tabsContainer, className)} role="tablist" {...props}>
      {items.map((item) => {
        const isSelected = value === item.value;
        const isDisabled = disabled || item.disabled;

        return (
          <div
            key={item.value}
            role="tab"
            tabIndex={isDisabled ? -1 : 0}
            aria-selected={isSelected}
            aria-disabled={isDisabled}
            className={tabItem}
            onClick={() => handleItemClick(item.value, item.disabled)}
            onKeyDown={(e) => handleKeyDown(e, item.value, item.disabled)}
          >
            <div className={tabItemFrame({ selected: isSelected, disabled: isDisabled })}>
              {renderItemContent(item, isSelected, isDisabled || false)}
            </div>
            <div className={tabItemIndicator({ selected: isSelected })} />
          </div>
        );
      })}
    </div>
  );
};

Tabs.displayName = 'Tabs';
